import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import useAssistedAffiliationStyles from "../styles";

type StatusChangeConfirmationModalProps = {
  isOpen: boolean;
  nextStatusLabel: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
};

const MAX_REASON_LENGTH = 500;

const StatusChangeConfirmationModal = ({
  isOpen,
  nextStatusLabel,
  onCancel,
  onConfirm,
}: StatusChangeConfirmationModalProps) => {
  const classes = useAssistedAffiliationStyles();
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setReason("");
      setSubmitted(false);
    }
  }, [isOpen]);

  const handleReasonChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setReason(event.target.value);
  };

  const handleConfirm = () => {
    setSubmitted(true);

    if (!reason.trim()) {
      return;
    }

    onConfirm(reason);
  };

  const reasonError = submitted && !reason.trim();

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      fullWidth
      aria-labelledby="status-change-title"
      classes={{ paper: classes.statusChangeModalPaper }}
    >
      <IconButton
        size="small"
        className={classes.modalClose}
        aria-label="Cerrar"
        onClick={onCancel}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent className={classes.statusChangeModalContent}>
        <Typography id="status-change-title" component="h2" className={classes.statusChangeModalTitle}>
          Cambiar estado de creación del comercio
        </Typography>

        <Typography className={classes.statusChangeModalDescription}>
          Estás por cambiar el estado a{" "}
          <span className={classes.statusChangeNextStatus}>{nextStatusLabel}</span>.
        </Typography>
        <Typography className={classes.statusChangeModalDescription}>
          Por favor, indica el motivo del cambio:
        </Typography>

        <Typography className={classes.statusChangeReasonLabel}>
          Motivo del cambio <span className={classes.requiredMark}>*</span>
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          variant="outlined"
          value={reason}
          onChange={handleReasonChange}
          error={reasonError}
          helperText={reasonError ? "El motivo del cambio es requerido." : " "}
          inputProps={{ maxLength: MAX_REASON_LENGTH }}
        />
        <Typography className={classes.statusChangeCounter}>
          {reason.length}/{MAX_REASON_LENGTH}
        </Typography>

        <Box className={classes.statusChangeInfoBox}>
          <InfoOutlinedIcon fontSize="small" />
          <Box>
            <Typography className={classes.statusChangeInfoTitle}>
              Este motivo se guardará en “Comentarios internos”
            </Typography>
            <Typography className={classes.statusChangeInfoText}>
              El motivo será visible en el campo de comentarios internos de esta solicitud.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions disableSpacing className={classes.modalActions}>
        <Button variant="outlined" className={classes.modalCancelButton} onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="contained" className={classes.modalConfirmButton} onClick={handleConfirm}>
          Confirmar cambio
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusChangeConfirmationModal;
